import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Button from "../../components/FormComponents/Button";
import { buildPath } from "../../routes/routesPath";
import { getActiveFilters } from "../Monitor/utils";
import useAuditPerforms from "./hooks/useAuditPerforms";
import useAuditFilters from "./hooks/useAuditFilters";
import useSubjectConfig from "./hooks/useSubjectConfig";
import AuditFilterSection from "./components/AuditFilterSection";
import AuditPerformsTable from "./components/AuditPerformsTable";
import "../Monitor/monitor.css";
import "./audit.css";

const INITIAL_FILTERS = {
  app_id: undefined,
  wf_id: undefined,
  activity_id: undefined,
  instance_id: undefined,
  user_email: undefined,
  status: undefined,
  from_date: undefined,
  to_date: undefined
};

const AuditPerforms = ({ embedded = false, orgId }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });

  const { performs, total, pageTrack, fetchPerforms } = useAuditPerforms();
  const { options, appNameById } = useAuditFilters(filters.app_id, filters.wf_id, orgId);

  // all audit queries are scoped to the current organization
  const scoped = (f) => ({ ...getActiveFilters(f), ...(orgId ? { org_id: orgId } : {}) });
  // always show the 3 subject columns (searchable); use a configured label when
  // one exists, otherwise a generic "Subject N"
  const { activeColumns } = useSubjectConfig(filters.app_id, filters.wf_id);
  const subjectColumns = [1, 2, 3].map((idx) => {
    const cfg = activeColumns.find((c) => c.idx === idx);
    return { idx, label: cfg?.label || `Identifier ${idx}` };
  });

  // debounce only free-text input (email); pickers apply immediately
  const debouncedFetchRef = useRef(null);
  useEffect(() => {
    debouncedFetchRef.current = debounce((updatedFilters, limit) => {
      fetchPerforms(1, limit, scoped(updatedFilters));
    }, 500);
    return () => debouncedFetchRef.current?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPerforms, orgId]);

  const applyFilters = (updatedFilters, { debounce: useDebounce } = {}) => {
    setFilters(updatedFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (useDebounce) {
      debouncedFetchRef.current?.(updatedFilters, pagination.limit);
    } else {
      debouncedFetchRef.current?.cancel();
      fetchPerforms(1, pagination.limit, scoped(updatedFilters));
    }
  };

  const handleFilterChange = (key, value, opts) => {
    applyFilters({ ...filters, [key]: value }, opts);
  };

  // cascade: changing app clears workflow/activity/instance; changing workflow clears activity/instance
  const handleAppChange = (appId) => {
    applyFilters({ ...filters, app_id: appId, wf_id: undefined, activity_id: undefined, instance_id: undefined });
  };

  const handleWfChange = (wfId) => {
    applyFilters({ ...filters, wf_id: wfId, activity_id: undefined, instance_id: undefined });
  };

  const handleRefresh = async () => {
    await fetchPerforms(pagination.page, pagination.limit, scoped(filters));
    toast.success("Audit performs refreshed");
  };

  const onPageChange = async (pageNumber) => {
    await fetchPerforms(pageNumber, pagination.limit, scoped(filters));
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const onLimitChange = async (newLimit) => {
    await fetchPerforms(1, newLimit, scoped(filters));
    setPagination({ page: 1, limit: newLimit });
  };

  useEffect(() => {
    fetchPerforms(1, 50, scoped(INITIAL_FILTERS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const content = (
    <div className={`d_flex flex_column gap_2 w_100 ${embedded ? "flex_1" : "h_100 p_3"}`} style={{ minHeight: 0 }}>
      {!embedded && (
        <div className="w_100 d_flex justify_between align_center">
          <h2>Audit</h2>
          <div className="d_flex gap_2 align_center">
            <span className="font_size_xsmall color_secondary">{total} performs</span>
            <Button variant="primary" onClick={handleRefresh} title="Refresh performs">
              Refresh
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="w_100 d_flex justify_end align_center">
          <span className="font_size_xsmall color_secondary mr_2">{total} performs</span>
          <Button variant="primary" onClick={handleRefresh} title="Refresh performs">
            Refresh
          </Button>
        </div>
      )}

      <AuditFilterSection
        filters={filters}
        options={options}
        onFilterChange={handleFilterChange}
        onAppChange={handleAppChange}
        onWfChange={handleWfChange}
        subjectLabel={activeColumns[0]?.label}
      />

      <AuditPerformsTable
        performs={performs}
        appNameById={appNameById}
        subjectColumns={subjectColumns}
        pageTrack={pageTrack}
        pagination={pagination}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        onOpen={(traceId) => navigate(buildPath.orgAuditTrace({ orgId, traceId }))}
      />
    </div>
  );

  if (embedded) return content;
  return (
    <div className="application_container">
      <AppLeftSideBar type="org" />
      {content}
    </div>
  );
};

export default AuditPerforms;
